import { App } from "vue";
import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import VueAxios from "vue-axios";
import JwtService from "@/core/services/JwtService";
import { AxiosResponse, AxiosRequestConfig } from "axios";
import ManagerEndpoints from "@/core/services/api/ManagerEndpoints";
import { PaginationResponse } from "@/core/services/api/interfaces/PaginationResponse";
import store from "@/store";
import { Mutations } from "@/store/enums/StoreEnums";
import Router from "@/router";
import Swal, { SweetAlertOptions } from "sweetalert2";
import { Transaction } from "@/core/services/api/interfaces/manager/Transaction";
import AdminEndpoints from "@/core/services/api/AdminEndpoints";
import CommonEndpoints from "@/core/services/api/CommonEndpoints";
import {
  getWindowIdentifier,
  windowIdentifierKey,
} from "@/core/services/WindowIdentifier";
import IErrors from "@/core/services/api/interfaces/local/IErrors";

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App;
  /**
   * @description endpoints
   */
  public static manager = new ManagerEndpoints();
  public static admin = new AdminEndpoints();
  public static common = new CommonEndpoints();

  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {
    ApiService.vueInstance = app;
    ApiService.vueInstance.use(VueAxios, axios);
    ApiService.vueInstance.axios.defaults.baseURL = process.env.VUE_APP_API_URL;
    ApiService.vueInstance.axios.defaults.headers.common["Accept"] =
      "application/json";
    ApiService.vueInstance.axios.defaults.headers.common["X-Client-Version"] =
      store.getters.getRelease;
    ApiService.vueInstance.axios.interceptors.request.use(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (config: InternalAxiosRequestConfig<any>) => {
        config.headers[windowIdentifierKey] = getWindowIdentifier();
        const token = JwtService.getToken();
        if (token) {
          config.headers["Authorization"] = `Bearer ${token.access_token}`;
        }
        if (store.getters.tfa_code) {
          config.headers["X-OTP-Code"] = store.getters.tfa_code;
          store.commit(Mutations.COMMON_AUTH_TFA_CODE_SET_SUBMITTING, true);
          store.commit(Mutations.COMMON_AUTH_TFA_CODE_SET, null);
        }

        return config;
      }
    );
    const isAuthRequired = (response: AxiosResponse): boolean => {
      return (
        403 === response?.status && "x-otp-code-required" in response.headers
      );
    };

    ApiService.vueInstance.axios.interceptors.response.use(
      (config) => {
        store.commit(Mutations.COMMON_AUTH_TFA_CODE_SET_SUBMITTING, false);
        store.commit(Mutations.COMMON_AUTH_TFA_CODE_CHANGE_STATE, false);

        return config;
      },
      async (error: AxiosError) => {
        store.commit(Mutations.COMMON_AUTH_TFA_CODE_SET_SUBMITTING, false);
        const { response } = error;
        if (!response || !("data" in response)) {
          return;
        }
        const { data } = response as AxiosResponse;
        if (500 === response?.status) {
          await Swal.fire({
            text: "Server Error, please try again later",
            icon: "error",
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          });

          return Promise.reject(error);
        }
        if (503 === response?.status) {
          if ("x-maintenance" in response.headers) {
            const { message } = Object.assign(
              { message: error.message || "Maintenance mode" },
              data
            );

            return Swal.fire({
              title: message,
              icon: "warning",
              showCancelButton: false,
              showDenyButton: false,
              showConfirmButton: false,
            } as SweetAlertOptions).then(() => Promise.reject(error));
          } else if ("x-remote-portal-status" in response.headers) {
            const { message } = Object.assign(
              {
                message:
                  error.message ||
                  "Remote portal is unavailable. Please try again later",
              },
              data
            );

            return Swal.fire({
              title: message,
              icon: "warning",
              showCancelButton: false,
              showDenyButton: false,
              showConfirmButton: false,
            } as SweetAlertOptions).then(() => Promise.reject(error));
          }
        }

        const authRequired = isAuthRequired(response as AxiosResponse);

        if (
          authRequired &&
          store.getters.is_tfa_required &&
          "message" in (data || {})
        ) {
          store.commit(Mutations.COMMON_AUTH_TFA_CODE_SET_ERROR, data.message);
        }
        if (
          401 === response?.status &&
          "name" in Router.currentRoute &&
          "login" !== Router.currentRoute.name
        ) {
          store.commit(Mutations.COMMON_AUTH_PURGE);
          await Router.push({ name: "login" });

          return Promise.reject(error);
        }

        store.commit(Mutations.COMMON_AUTH_TFA_CODE_CHANGE_STATE, authRequired);

        return Promise.reject(error);
      }
    );
  }

  /**
   * @description send the GET HTTP request
   * @returns Promise<AxiosResponse>
   */
  public static query(
    resource: string,
    params: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, params);
  }

  /**
   * @description send the GET HTTP request
   * @returns Promise<AxiosResponse>
   */
  public static get(
    resource: string,
    params: AxiosRequestConfig = {}
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, params);
  }

  /**
   * @description set the POST HTTP request
   * @returns Promise<AxiosResponse>
   */
  public static post(
    resource: string,
    params: AxiosRequestConfig = {}
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.post(resource, params);
  }

  public static direct(
    params: AxiosRequestConfig = {}
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios(params);
  }

  /**
   * @description send the UPDATE HTTP request
   * @returns Promise<AxiosResponse>
   */
  public static update(
    resource: string,
    slug: string,
    params: AxiosRequestConfig = {}
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params);
  }

  /**
   * @description Send the PUT HTTP request
   * @returns Promise<AxiosResponse>
   */
  public static put(
    resource: string,
    params: AxiosRequestConfig = {}
  ): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}`, params);
  }

  /**
   * @description Send the DELETE HTTP request
   * @returns Promise<AxiosResponse>
   */
  public static delete(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.delete(resource);
  }

  public static asFileDownload(response: AxiosResponse, filename: string) {
    return Promise.resolve(response).then((response: AxiosResponse) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
    });
  }

  public static async unwrapItemResponse<T>(
    response: AxiosResponse
  ): Promise<T> {
    return Promise.resolve(response)
      .then(({ data }) => data)
      .then(({ data = {} }) => data);
  }

  public static async unwrapItemsResponse<T>(
    response: AxiosResponse
  ): Promise<T[]> {
    return Promise.resolve(response)
      .then(({ data }) => data)
      .then(({ data = [] }) => data);
  }

  public static unwrapErrors(error: AxiosError): IErrors {
    const response = error.response;
    if (!response || !("status" in response) || !("data" in response)) {
      return {};
    }
    if (422 !== response.status || !response.data) {
      return {};
    }

    const data = (response.data || {}) as {
      errors?: { [index: string]: string[] };
    };

    return data.errors || {};
  }

  public static unwrapErrorMessage(error: AxiosError): string {
    const response = error.response;
    if (!response || !("status" in response) || !("data" in response)) {
      return "Unknown error";
    }
    if (422 !== response.status || !response.data) {
      return "Unknown error";
    }

    const data = (response.data || {}) as {
      message?: string;
    };

    return data.message || "Unknown error";
  }

  public static async unwrapPaginationResponse<T>(
    response: AxiosResponse
  ): Promise<PaginationResponse<T>> {
    return Promise.resolve(response)
      .then(({ data }) => data)
      .then((response) => {
        const { data } = response;

        if (!("meta" in response) || !response.meta) {
          return new PaginationResponse(data);
        }

        const meta = response.meta;
        const { current_page, last_page, per_page, path, from, to, total } =
          meta;

        return new PaginationResponse(
          data,
          current_page,
          last_page,
          per_page,
          path,
          from,
          to,
          total
        );
      });
  }

  public static async setTransactionSourceOfFunds(transaction: Transaction) {
    return Swal.fire({
      title: "Select source of funds",
      input: "select",
      icon: "question",
      inputOptions: {
        1: "Business",
        2: "Customer",
      },
      inputPlaceholder: "required",
      showCancelButton: true,
    } as SweetAlertOptions).then(function (result) {
      if (!result.isConfirmed) {
        return;
      }
      if (!result.value || !~[1, 2].indexOf(parseInt(result.value))) {
        return console.error("Invalid value given", result.value);
      }
      ApiService.manager.transaction
        .updateSourceOfFunds(transaction.id, {
          source_id: result.value,
        } as AxiosRequestConfig)
        .then(ApiService.unwrapItemResponse<Transaction>)
        .then(async (transaction) => {
          await Swal.fire({
            icon: "success",
            html: `Transaction ${transaction.name} updated successfully!`,
          });
        });
    });
  }
}

export default ApiService;
